/**
 * S-03 — Import BPML & versions (Admin) — US-01, Lot 3.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  bpmlApi,
  domainsApi,
  ApiRequestError,
  type BpmlImportResult,
  type BpmlLineDto,
  type BpmlVersionDto,
  type DomainDto,
} from "../api/api";

export function BpmlImportPage() {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<BpmlVersionDto[]>([]);
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lines, setLines] = useState<BpmlLineDto[]>([]);
  const [domainFilter, setDomainFilter] = useState("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<BpmlImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadLines(versionId: string | null, domain: string) {
    if (!versionId) {
      setLines([]);
      return;
    }
    const res = await bpmlApi.listLines(versionId, domain || undefined);
    setLines(res.lines);
  }

  async function refresh(preferId?: string | null) {
    setError(null);
    try {
      const [domRes, verRes] = await Promise.all([domainsApi.list(), bpmlApi.listVersions()]);
      setDomains(domRes.domains);
      setVersions(verRes.versions);
      const next =
        preferId ??
        verRes.versions.find((v) => v.id === selectedId)?.id ??
        verRes.versions.find((v) => v.active)?.id ??
        verRes.versions[0]?.id ??
        null;
      setSelectedId(next);
      await loadLines(next, domainFilter);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadLines(selectedId, domainFilter).catch((err) => {
      setError(err instanceof ApiRequestError ? err.message : t("load_lines_failed"));
    });
  }, [selectedId, domainFilter]);

  async function downloadTemplate() {
    setError(null);
    try {
      const blob = await bpmlApi.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "skillmap-bpml-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("download_failed"));
    }
  }

  async function onImport(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setError(t("choose_xlsx"));
      return;
    }
    if (!label.trim()) {
      setError(t("version_label_required"));
      return;
    }
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const result = await bpmlApi.import(file, label.trim());
      setReport(result);
      setFile(null);
      setLabel("");
      await refresh(result.version.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("import_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onActivate(id: string) {
    if (!window.confirm(t("confirm_activate"))) return;
    setBusy(true);
    setError(null);
    try {
      await bpmlApi.activate(id);
      await refresh(id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("activate_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <div>
          <h1>{t("bpml_ref_title")}</h1>
          <p>{t("bpml_import_help")}</p>
        </div>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="sm-form-card">
        <h2>{t("new_import")}</h2>
        <button type="button" className="sm-btn sm-btn-secondary" onClick={() => void downloadTemplate()}>
          ⬇ {t("download_excel_template")}
        </button>
        <form onSubmit={onImport}>
          <div className="sm-form-actions" style={{ flexWrap: "wrap" }}>
            <input
              className="sm-input"
              required
              placeholder={t("version_label_ph")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={{ minWidth: 240 }}
            />
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
              {busy ? t("import_ellipsis") : t("import")}
            </button>
          </div>
        </form>
        {report ? (
          <p className="sm-muted" style={{ marginTop: 12 }}>
            {t("import_ok", {
              imported: report.imported,
              domains: report.domains.join(", ") || "—",
              carriedOver: report.carriedOver,
            })}
          </p>
        ) : null}
      </div>

      <div className="sm-section">
        <h2>{t("versions")}</h2>
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("version_label")}</th>
                <th>{t("imported_on")}</th>
                <th>{t("line_count")}</th>
                <th>{t("domain")}</th>
                <th>{t("status")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {versions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="sm-muted">
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                versions.map((v) => (
                  <tr
                    key={v.id}
                    style={{
                      background: selectedId === v.id ? "rgba(31,78,121,0.08)" : undefined,
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedId(v.id)}
                  >
                    <td>{v.label}</td>
                    <td>{new Date(v.importedAt).toLocaleString()}</td>
                    <td>{v.lineCount}</td>
                    <td>{v.domainCount}</td>
                    <td>
                      {v.active ? (
                        <span className="sm-badge">{t("active")}</span>
                      ) : (
                        <span className="sm-muted">{t("status_inactive")}</span>
                      )}
                    </td>
                    <td>
                      {!v.active ? (
                        <button
                          type="button"
                          className="sm-btn sm-btn-primary"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            void onActivate(v.id);
                          }}
                        >
                          {t("activate")}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sm-section">
        <div className="sm-toolbar" style={{ justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>{t("line_count")}</h2>
          <select
            className="sm-input"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            <option value="">{t("all_domains")}</option>
            {domains.map((d) => (
              <option key={d.id} value={d.code}>
                {d.code}
              </option>
            ))}
          </select>
        </div>
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("critical_col")}</th>
                <th>{t("domain")}</th>
                <th>Macro</th>
                <th>Business Process</th>
                <th>Category</th>
                <th>Sub Process</th>
                <th>Task</th>
                <th>ERP</th>
                <th>Cplx</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={9} className="sm-muted">
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                lines.map((line) => (
                  <tr key={line.id}>
                    <td>
                      {line.isCritical ? (
                        <span className="sm-badge">{t("critical")}</span>
                      ) : (
                        <span className="sm-muted">{t("no")}</span>
                      )}
                    </td>
                    <td>{line.domain}</td>
                    <td>{line.macroProcess}</td>
                    <td>{line.businessProcess}</td>
                    <td>{line.processCategory}</td>
                    <td>{line.subProcess ?? "—"}</td>
                    <td>{line.task ?? "—"}</td>
                    <td>—</td>
                    <td>{line.complexityIndex}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
